import {
  AddAPhoto,
  Check,
  Close,
  Delete,
  Edit,
  Image as ImageIcon,
  InsertDriveFile,
  PictureAsPdf,
  UploadFile,
} from "@mui/icons-material"
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { CameraDialog } from "components/Camera/CameraDialog"
import { EUTDataCollapsible } from "components/EUTManagementLeftBox/EUTDataCollapsible"
import { HaufeIcon } from "components/HaufeIcon/HaufeIcon"
import { ImageThumbnail } from "components/ImageThumbnail/ImageThumbnail"
import {
  Device,
  DeviceDataGroup,
  DeviceEut,
  DeviceEutGroup,
  DeviceSetup,
  DeviceType,
  ExamCalc,
  Examination,
  File,
  FileType,
  FileUploadInput,
  Laboratory,
  Norm,
  Project,
  Status,
  Task,
  User,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { FormEvent, SyntheticEvent, useEffect, useState } from "react"
import { getPDFUrl } from "store/pdfService"
import { emptyPerson } from "store/store"
import { isDeviceAux, isEutGroup } from "store/typeGuards"
import { autoGroupDeviceIntoTestSetups } from "utils/autoTestSetupGrouping"
import { treoLocationList } from "utils/constants"
import { getImageURL } from "utils/getFileURL"
import { getNormName } from "utils/getNormName"
import { getOperatingModes } from "utils/getOperatingModes"
import { normalizeNumberInput } from "utils/numberInputHelpers"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"
import { AddNormsToProjectDialog } from "./AddNormsToProjectDialog"

type DeviceDetailsProps = {
  otherDevices: Device[]
  device: Device
  project: Project
  onChange: (device: Device | DeviceEutGroup) => void
  onCancel: () => void
  onSave: (deviceToSave: Device | DeviceEutGroup) => void
}

type ValidationErrors = {
  name?: string
}

export const emptyLaboratory: Laboratory = {
  id: "",
  name: "",
  manager: "",
}

export const DeviceDetails = (props: DeviceDetailsProps) => {
  const { device, otherDevices, project, onChange, onCancel, onSave } = props
  const { deviceDataGroups, examinationConfigs } = useStoreState((state) => state)
  const { fetchExaminationConfigs, fetchDeviceDataGroups, uploadFile } = useStoreActions(
    (actions) => actions
  )
  const [user] = useLocalStorage<User>("user", emptyPerson)
  const [openAddNorms, setOpenAddNorms] = useState<boolean>(false)
  const [numberOfEUT, setNumberOfEUT] = useState<number>(1)
  const [updatedDataGroup, setUpdatedDataGroup] = useState<DeviceDataGroup[]>([])
  const [dataGroupsOptions, setDataGroupsOptions] = useState<DeviceDataGroup[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [openCameraDialog, setOpenCameraDialog] = useState<boolean>(false)
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false)
  const [editingImage, setEditingImage] = useState<File | null>(null)
  const [editImageName, setEditImageName] = useState("")
  const [editImageDescription, setEditImageDescription] = useState("")
  const [isUploadingDocument, setIsUploadingDocument] = useState<boolean>(false)
  const [editingDocument, setEditingDocument] = useState<File | null>(null)
  const [editDocumentName, setEditDocumentName] = useState("")
  const [editDocumentDescription, setEditDocumentDescription] = useState("")
  const [previewImage, setPreviewImage] = useState<File | null>(null)

  const validateDevice = (): ValidationErrors => {
    const errors: ValidationErrors = {}

    if (!device.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    return errors
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  const operatingModesRequired =
    isEutGroup(device) &&
    device.deviceDataGroup.some((dg) => dg.deviceData.some((dd) => dd.isOperatingMode))

  const providedOperatingModesCount = isEutGroup(device)
    ? device.deviceDataGroup
        .flatMap((dg) => dg.deviceData)
        .filter((dd) => dd.isOperatingMode && dd.data)
        .flatMap((dd) => dd.data!)
        .map((d) => d.value)
        .filter(Boolean).length
    : 0

  const isSaveDisabled =
    hasValidationErrors || (operatingModesRequired && providedOperatingModesCount === 0)

  useEffect(() => {
    if (isEutGroup(device) || isDeviceAux(device)) {
      const filteredGroups = deviceDataGroups.filter(
        (item) => !device.deviceDataGroup.some((i) => i.id === item.id)
      )
      setDataGroupsOptions(filteredGroups)
    }
  }, [device, deviceDataGroups])

  useEffect(() => {
    const loadExaminationConfigs = async () => {
      try {
        await fetchExaminationConfigs({ fresh: false })
      } catch (err) {
        console.error("Error loading examination configs:", err)
      }
    }
    loadExaminationConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const loadDeviceDataGroups = async () => {
      try {
        await fetchDeviceDataGroups({ fresh: false })
      } catch (err) {
        console.error("Error loading device data groups:", err)
      }
    }
    loadDeviceDataGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEutGroup(device)) {
      setNumberOfEUT(device.devices.length)
    }
    if (isDeviceAux(device)) {
      setNumberOfEUT(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEutGroup(device) && (device as DeviceEutGroup).examinations) {
      const newUpdatedDataGroup: DeviceDataGroup[] = []
      ;(device as DeviceEutGroup).examinations.forEach((exam) => {
        const exConfig = examinationConfigs.find((i) => i.id === exam.examinationConfig.id)
        if (exConfig) {
          newUpdatedDataGroup.push(...exConfig.eutDataGroups)
        }
      })
      // Remove duplicates by using Map with id as key
      const uniqueDataGroups = Array.from(
        new Map(newUpdatedDataGroup.map((item) => [item.id, item])).values()
      )
      setUpdatedDataGroup(uniqueDataGroups)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(device as DeviceEutGroup).examinations, examinationConfigs])

  useEffect(() => {
    const newDeviceDataGroup: DeviceDataGroup[] = isEutGroup(device)
      ? deviceDataGroups.filter((item) => item.isDefault)
      : deviceDataGroups.filter((item) => item.isAux)

    if (
      isEutGroup(device) &&
      device.deviceDataGroup.length <= 0 &&
      JSON.stringify(device.deviceDataGroup) !== JSON.stringify(newDeviceDataGroup)
    ) {
      onChange({
        ...device,
        deviceDataGroup: newDeviceDataGroup,
      })
    }

    if (isDeviceAux(device) && device.deviceDataGroup.length <= 0) {
      onChange({
        ...device,
        deviceDataGroup: newDeviceDataGroup,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.type, deviceDataGroups])

  const handleChange = (field: string, value: string) => {
    onChange({
      ...device,
      [field]: value,
    })

    // Clear validation error for this field if it's now valid
    if (field === "name" && value.trim() && validationErrors.name) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.name
        return newErrors
      })
    }
  }

  const handleChangeNormsAndExams = (
    value: {
      norm: Norm
      examinations: {
        examCalc: ExamCalc
        operatingModes: string[]
        examinationId: string
        salesQuote: string
      }[]
    }[]
  ) => {
    const updatedNorms = value.map((item) => item.norm)
    const updatedExams: Examination[] = []
    const newDeviceDataGroups: DeviceDataGroup[] = []

    value.forEach((v) => {
      v.examinations.forEach((e) => {
        const exConfig = examinationConfigs.find((i) => i.id === e.examCalc.examinationConfig.id)
        if (exConfig) {
          // Check if this examination already exists in the device
          const existingExam = isEutGroup(device)
            ? device.examinations.find(
                (existing) => existing.name === e.examCalc.name && existing.norm.id === v.norm.id
              )
            : undefined

          if (existingExam) {
            // If it exists, use the new examination ID and reset salesQuote
            updatedExams.push({
              ...existingExam,
              id: e.examinationId, // Use the new ID from dialog
              salesQuote: e.salesQuote, // Use the salesQuote from dialog
              // Update any properties that might have changed
              isAccredited: e.examCalc.isAccredited,
              calculation: e.examCalc.calculation,
              location: e.examCalc.location || treoLocationList[0],
              examinationConfig: exConfig,
            })
          } else {
            // Check if it exists in another Device of this project
            const otherEUTGroups: DeviceEutGroup[] = otherDevices.filter(
              (device) => device.type === DeviceType.EUT_GROUP
            ) as DeviceEutGroup[]
            const existingExamInOtherEUTGroups: Examination | undefined = otherEUTGroups
              .flatMap((device) => device.examinations)
              .find((exam) => exam.name === e.examCalc.name && exam.norm.id === v.norm.id)

            if (existingExamInOtherEUTGroups) {
              // Create new tasks without copying testDatas from existing examination
              const newTasks: Task[] = exConfig.tasks.map((task) => {
                return {
                  id: task.id,
                  name: task.name,
                  info: task.info,
                  taskSteps: task.taskSteps,
                  estimatedTime: 0,
                  workingTime: 0,
                  status: Status.OPEN,
                  examinator: emptyPerson,
                  testDatas: [], // Always start with empty testDatas for new examination
                }
              })

              // Start with empty deviceSetup; devices will be added via scanning
              const deviceSetup: DeviceSetup[] = []

              updatedExams.push({
                id: e.examinationId, // Use the new ID from dialog
                name: e.examCalc.name,
                norm: v.norm,
                isAccredited: e.examCalc.isAccredited,
                calculation: e.examCalc.calculation,
                numberOfRuns: isEutGroup(device) ? getOperatingModes(device).length : 0,
                examinationConfig: exConfig,
                status: Status.OPEN,
                tasks: newTasks, // Use new tasks without copied testDatas
                deviceSetup: deviceSetup,
                specialArrangements: "",
                salesQuote: e.salesQuote, // Use the salesQuote from dialog
                history: [],
                testParameterBlock: [],
                laboratory: exConfig.laboratory || emptyLaboratory,
                location: e.examCalc.location || treoLocationList[0],
                operatingModes: isEutGroup(device) ? getOperatingModes(device) : [],
                passFailCriteria: [],
              })
            } else {
              // If it's new, create a new examination with the ID from dialog

              const newTasks: Task[] = exConfig.tasks.map((task) => {
                return {
                  id: task.id,
                  name: task.name,
                  info: task.info,
                  taskSteps: task.taskSteps,
                  estimatedTime: 0,
                  workingTime: 0,
                  status: Status.OPEN,
                  examinator: emptyPerson,
                  testDatas: [],
                  // operatingModes: isEutGroup(device) ? getOperatingModes(device) : [],
                }
              })

              // Validate that norm is not null
              if (!v.norm) {
                console.error("Cannot create examination without norm", {
                  examCalc: e.examCalc,
                  norm: v.norm,
                })
                return
              }

              // Start with empty deviceSetup; devices will be added via scanning
              const deviceSetup: DeviceSetup[] = []

              const newExam: Examination = {
                id: e.examinationId, // Use the ID from dialog instead of generating new one
                name: e.examCalc.name,
                norm: v.norm,
                isAccredited: e.examCalc.isAccredited,
                calculation: e.examCalc.calculation,
                numberOfRuns: isEutGroup(device) ? getOperatingModes(device).length : 0,
                examinationConfig: exConfig!,
                status: Status.OPEN,
                tasks: newTasks,
                deviceSetup: deviceSetup,
                specialArrangements: "",
                salesQuote: e.salesQuote, // Use the salesQuote from dialog
                history: [],
                testParameterBlock: [],
                laboratory: exConfig.laboratory || emptyLaboratory,
                location: e.examCalc.location || treoLocationList[0],
                operatingModes: isEutGroup(device) ? getOperatingModes(device) : [],
                passFailCriteria: [],
              }
              updatedExams.push(newExam)
            }
          }
          newDeviceDataGroups.push(...exConfig.eutDataGroups)
        }
      })
    })

    if (isEutGroup(device)) {
      let filteredNorms: Norm[] = []
      let filteredExams: Examination[] = []
      let filteredDataGroup: DeviceDataGroup[] = []

      if (device.norms && device.norms.length > 0) {
        filteredNorms = updatedNorms.filter(
          (n) => !device.norms.flatMap((i) => i.id).includes(n.id)
        )
      } else {
        filteredNorms = updatedNorms
      }

      filteredExams = updatedExams

      if (device.deviceDataGroup && device.deviceDataGroup.length > 0) {
        filteredDataGroup = newDeviceDataGroups.filter(
          (dg) => !device.deviceDataGroup.flatMap((i) => i.id).includes(dg.id)
        )
      } else {
        filteredDataGroup = newDeviceDataGroups
      }

      const filteredDataGroupUniqueById = Array.from(
        new Map(filteredDataGroup.map((item) => [item.id, item])).values()
      )

      onChange({
        ...device,
        norms: device.norms
          ? [
              ...device.norms.filter((n) => updatedNorms.some((n2) => n2.id === n.id)),
              ...filteredNorms,
            ]
          : filteredNorms,
        examinations: [
          ...device.examinations.filter((e) =>
            updatedExams.some((e2) => e2.name === e.name && e2.norm.id === e.norm.id)
          ),
          ...filteredExams.filter(
            (e) =>
              !device.examinations.some(
                (existing) => existing.name === e.name && existing.norm.id === e.norm.id
              )
          ),
        ],
        deviceDataGroup: [...device.deviceDataGroup, ...filteredDataGroupUniqueById],
      })
    }
  }

  const isInExaminations = (dataGroup: DeviceDataGroup) => {
    return updatedDataGroup.some((dg2) => dg2.id === dataGroup.id)
  }

  const onSaveGroup = (arg: DeviceDataGroup): void => {
    if (isEutGroup(device) || isDeviceAux(device)) {
      const newDevice: Device | DeviceEutGroup = {
        ...device,
        deviceDataGroup: device.deviceDataGroup.map((group) => (group.id === arg.id ? arg : group)),
      }

      const deviceDatas = arg.deviceData.filter((item) => "data" in item && item.isOperatingMode)

      if (isEutGroup(newDevice) && deviceDatas.length > 0) {
        const operatingModes = deviceDatas
          .flatMap((item) => item.data)
          .flatMap((item) => item.value)

        if (operatingModes.length > 0) {
          const newExams = newDevice.examinations.map((exam) => {
            const isOperating = exam.examinationConfig.eutDataGroups
              .flatMap((i) => i.deviceData)
              .filter((j) => j.isOperatingMode)

            if (isOperating.length > 0) {
              return {
                ...exam,
                operatingModes: operatingModes,
              }
            }
            return exam
          })
          newDevice.examinations = newExams
        }
      }
      onChange(newDevice)
    }
  }

  const handleDeleteExamination = (examinationId: string) => {
    if (isEutGroup(device)) {
      const updatedExaminations = device.examinations.filter((exam) => exam.id !== examinationId)

      // Also remove related norms if no other examinations use them
      const usedNormIds = updatedExaminations.map((exam) => exam.norm.id)
      const updatedNorms = device.norms?.filter((norm) => usedNormIds.includes(norm.id)) || []

      onChange({
        ...device,
        examinations: updatedExaminations,
        norms: updatedNorms,
      })
    }
  }

  const handleSave = () => {
    // Validate before saving
    const errors = validateDevice()
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    let deviceToSave = { ...device }

    if (isEutGroup(device)) {
      const newDevices: DeviceEut[] = Array.from({ length: numberOfEUT }, (_, i) => ({
        ...(device.devices && device.devices.length > 0
          ? device.devices[0]
          : {
              id: v4(),
              type: DeviceType.EQUIPMENT_UNDER_TEST,
              name: `New EUT ${i + 1}`,
              barcode: `${device.barcode}-${i + 1}`,
              description: "",
              images: [],
              documents: [],
              note: [],
              movementData: [],
              manufacturer: device.manufacturer || undefined,
              model: device.model || undefined,
              serialNumber: device.serialNumber ? `${device.serialNumber}-${i + 1}` : undefined,
              function: device.function || undefined,
              productNumber: device.productNumber || undefined,
              revision: device.revision || undefined,
              software: device.software || undefined,
              softwareVersion: device.softwareVersion || undefined,
            }),
        id: v4(),
        barcode: `${device.barcode}-${i + 1}`,
      }))

      // Update numberOfRuns for all examinations based on current operating modes
      const currentOperatingModesCount = getOperatingModes(device).length
      const updatedExaminations = device.examinations.map((exam) => ({
        ...exam,
        numberOfRuns: currentOperatingModesCount,
      }))

      deviceToSave = {
        ...device,
        devices: newDevices,
        examinations: updatedExaminations,
      } as DeviceEutGroup

      // Apply automatic test setup grouping only for NEW EUT groups
      if (isEutGroup(deviceToSave) && device.id === "NEW") {
        deviceToSave = autoGroupDeviceIntoTestSetups(deviceToSave, project)
      }

      onChange(deviceToSave)
    }
    onSave(deviceToSave)
  }

  const handleAddDeviceDataGroup = (
    _: SyntheticEvent<Element, Event>,
    value: DeviceDataGroup | null
  ) => {
    if (value && isEutGroup(device)) {
      onChange({
        ...device,
        deviceDataGroup: [...device.deviceDataGroup, value],
      })
    }
  }

  const handleDeleteDeviceDataGroup = (dataGroup: DeviceDataGroup) => {
    if (isEutGroup(device)) {
      onChange({
        ...device,
        deviceDataGroup: device.deviceDataGroup.filter((dg) => dg.id !== dataGroup.id),
      })
    }
  }

  const handleCameraCapture = async (event: FormEvent<HTMLFormElement>, fotos: string[]) => {
    event.preventDefault()
    if (!fotos.length) {
      enqueueSnackbar("Kein Bild erfasst!", { variant: "error" })
      return
    }

    setIsUploadingImage(true)
    try {
      const formData = new FormData(event.currentTarget)
      const uploadImage: FileUploadInput = {
        id: "NEW",
        filename: v4() + ".png",
        name: (formData.get("title") as string) || "Camera Photo",
        description: (formData.get("description") as string) || undefined,
        base64: fotos[0],
        type: FileType.IMAGE,
        createdAt: new Date().toISOString(),
        createdBy: user.id,
      }

      const imageId = await uploadFile(uploadImage)

      const newImage: File = {
        id: imageId,
        createdAt: uploadImage.createdAt,
        createdBy: uploadImage.createdBy,
        fileURL: "",
        name: uploadImage.name,
        description: uploadImage.description,
        type: uploadImage.type,
        filename: uploadImage.filename,
      }

      // Add image to device
      onChange({
        ...device,
        images: [...(device.images || []), newImage],
      })

      enqueueSnackbar("Foto erfolgreich aufgenommen", { variant: "success" })
    } catch (error) {
      console.error("Error capturing photo:", error)
      enqueueSnackbar("Fehler beim Aufnehmen des Fotos", { variant: "error" })
    } finally {
      setIsUploadingImage(false)
      setOpenCameraDialog(false)
    }
  }

  const handleDeleteImage = (imageId: string) => {
    onChange({
      ...device,
      images: device.images?.filter((img) => img.id !== imageId) || [],
    })
  }

  const handleEditImage = (image: File) => {
    setEditingImage(image)
    setEditImageName(image.name)
    setEditImageDescription(image.description || "")
  }

  const handleOpenImage = (image: File) => {
    setPreviewImage(image)
  }

  const handleSaveImageEdit = () => {
    if (!editingImage) return

    const updatedImages =
      device.images?.map((img) => {
        if (img.id === editingImage.id) {
          return {
            ...img,
            name: editImageName,
            description: editImageDescription || undefined,
          }
        }
        return img
      }) || []

    onChange({
      ...device,
      images: updatedImages,
    })

    setEditingImage(null)
    enqueueSnackbar("Bild aktualisiert", { variant: "success" })
  }

  const handleCancelImageEdit = () => {
    setEditingImage(null)
    setEditImageName("")
    setEditImageDescription("")
  }

  // Document handling functions
  const handleDocumentUpload = async (files: globalThis.File[]) => {
    if (files.length === 0) return

    setIsUploadingDocument(true)
    try {
      const file = files[0]
      const reader = new FileReader()

      reader.onloadend = async () => {
        const base64 = reader.result as string
        const fileType = file.type.includes("pdf") ? FileType.PDF : FileType.OTHER

        const uploadDocument: FileUploadInput = {
          id: "NEW",
          filename: `${v4()}-${file.name}`,
          name: file.name,
          description: undefined,
          base64: base64,
          type: fileType,
          createdAt: new Date().toISOString(),
          createdBy: user.id,
        }

        const documentId = await uploadFile(uploadDocument)

        const newDocument: File = {
          id: documentId,
          createdAt: uploadDocument.createdAt,
          createdBy: uploadDocument.createdBy,
          fileURL: "",
          name: uploadDocument.name,
          description: uploadDocument.description,
          type: uploadDocument.type,
          filename: uploadDocument.filename,
        }

        // Add document to device
        onChange({
          ...device,
          documents: [...(device.documents || []), newDocument],
        })

        enqueueSnackbar("Dokument erfolgreich hochgeladen", { variant: "success" })
      }

      reader.readAsDataURL(file)
    } catch (error) {
      console.error("Error uploading document:", error)
      enqueueSnackbar("Fehler beim Hochladen des Dokuments", { variant: "error" })
    } finally {
      setIsUploadingDocument(false)
    }
  }

  const handleDeleteDocument = (documentId: string) => {
    onChange({
      ...device,
      documents: device.documents?.filter((doc) => doc.id !== documentId) || [],
    })
  }

  const handleEditDocument = (document: File) => {
    setEditingDocument(document)
    setEditDocumentName(document.name)
    setEditDocumentDescription(document.description || "")
  }

  const handleOpenDocument = async (document: File) => {
    try {
      if (document.type === FileType.PDF) {
        const url = await getPDFUrl(document.id)
        window.open(url, "_blank", "noopener,noreferrer")
        return
      }
      if (document.type === FileType.IMAGE) {
        setPreviewImage(document)
        return
      }
      if (document.fileURL) {
        window.open(document.fileURL, "_blank", "noopener,noreferrer")
        return
      }
      enqueueSnackbar("Datei kann nicht geöffnet werden", { variant: "warning" })
    } catch (error) {
      console.error("Error opening document:", error)
      enqueueSnackbar("Fehler beim Öffnen der Datei", { variant: "error" })
    }
  }

  const handleSaveDocumentEdit = () => {
    if (!editingDocument) return

    const updatedDocuments =
      device.documents?.map((doc) => {
        if (doc.id === editingDocument.id) {
          return {
            ...doc,
            name: editDocumentName,
            description: editDocumentDescription || undefined,
          }
        }
        return doc
      }) || []

    onChange({
      ...device,
      documents: updatedDocuments,
    })

    setEditingDocument(null)
    enqueueSnackbar("Dokument aktualisiert", { variant: "success" })
  }

  const handleCancelDocumentEdit = () => {
    setEditingDocument(null)
    setEditDocumentName("")
    setEditDocumentDescription("")
  }

  // Helper function to group examinations by norm
  const groupExaminationsByNorm = () => {
    if (!isEutGroup(device) || !device.examinations || device.examinations.length === 0) {
      return {}
    }
    return device.examinations.reduce(
      (
        acc: Record<string, { norm: Norm; examinations: Examination[] }>,
        examination: Examination
      ) => {
        const normKey = examination.norm.id
        if (!acc[normKey]) {
          acc[normKey] = {
            norm: examination.norm,
            examinations: [],
          }
        }
        acc[normKey].examinations.push(examination)
        return acc
      },
      {} as Record<string, { norm: Norm; examinations: Examination[] }>
    )
  }

  const groupedExaminations = groupExaminationsByNorm()

  return (
    <Card
      sx={{
        width: "100%",
      }}
    >
      <CardHeader
        title={
          <Typography variant="h6" sx={{ color: "white" }}>
            {device.name !== ""
              ? device.name
              : isEutGroup(device)
                ? "Bitte EUT Bezeichnung festlegen"
                : "Bitte Device Bezeichnung festlegen"}
          </Typography>
        }
        subheader={device.barcode}
      />
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie folgende Fehler:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.name && <li>{validationErrors.name}</li>}
            </ul>
          </Alert>
        )}

        <TextField
          label={
            isEutGroup(device) ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                EUT-Bezeichnung* <HaufeIcon />
              </Box>
            ) : (
              "Device Bezeichnung"
            )
          }
          value={device.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
          placeholder={
            isEutGroup(device)
              ? "Bitte EUT Bezeichnung festlegen"
              : "Bitte Device Bezeichnung festlegen"
          }
        />

        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              overflowY: "auto",
              maxHeight: "calc(100vh - 250px)",
              mb: 2,
              pb: 10, // Add padding bottom to account for sticky CardActions
            }}
          >
            <TextField
              label={
                isEutGroup(device) ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Intended Use (bitte auf Englisch für Bericht) <HaufeIcon />
                  </Box>
                ) : (
                  "Beschreibung"
                )
              }
              value={device.description}
              multiline
              rows={4}
              onChange={(e) => handleChange("description", e.target.value)}
              sx={{
                marginTop: 1,
              }}
            />

            <TextField
              label="Hersteller"
              value={device.manufacturer || ""}
              onChange={(e) => handleChange("manufacturer", e.target.value)}
            />

            <TextField
              label="Modell"
              value={device.model || ""}
              onChange={(e) => handleChange("model", e.target.value)}
            />

            <TextField
              label="Seriennummer"
              value={device.serialNumber || ""}
              onChange={(e) => handleChange("serialNumber", e.target.value)}
            />

            {!isEutGroup(device) && (
              <TextField
                label="Funktion"
                value={device.function || ""}
                onChange={(e) => handleChange("function", e.target.value)}
                multiline
                rows={2}
              />
            )}

            {isEutGroup(device) && (
              <>
                <TextField
                  label="Produktnummer"
                  value={device.productNumber || ""}
                  onChange={(e) => handleChange("productNumber", e.target.value)}
                />

                <TextField
                  label="Revision"
                  value={device.revision || ""}
                  onChange={(e) => handleChange("revision", e.target.value)}
                />

                <TextField
                  label="Software"
                  value={device.software || ""}
                  onChange={(e) => handleChange("software", e.target.value)}
                />

                <TextField
                  label="Software Version"
                  value={device.softwareVersion || ""}
                  onChange={(e) => handleChange("softwareVersion", e.target.value)}
                />
              </>
            )}

            {isEutGroup(device) && (
              <TextField
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Annahmen <HaufeIcon />
                  </Box>
                }
                value={device.assumptions}
                multiline
                rows={4}
                onChange={(e) => handleChange("assumptions", e.target.value)}
              />
            )}

            {isEutGroup(device) && (
              <TextField
                label={"Anzahl der EUT"}
                type="number"
                value={numberOfEUT || ""}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === "") {
                    setNumberOfEUT(0) // Will show as empty due to || ""
                  } else {
                    const numValue = parseInt(value)
                    setNumberOfEUT(isNaN(numValue) ? 0 : Math.max(1, numValue))
                  }
                }}
                onBlur={(e) => {
                  const normalizedValue = normalizeNumberInput(e.target.value)
                  const validValue = Math.max(1, normalizedValue) // Ensure minimum of 1
                  if (validValue !== numberOfEUT) {
                    setNumberOfEUT(validValue)
                  }
                }}
                inputProps={{
                  min: 1,
                  step: 1,
                }}
              />
            )}

            {/* Photo Upload Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Fotos / Bilder
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <StyledButton
                  buttonType="custom"
                  customIcon={<AddAPhoto />}
                  onClick={() => setOpenCameraDialog(true)}
                  variant="contained"
                  disabled={isUploadingImage}
                >
                  Bild hochladen
                </StyledButton>
              </Box>

              {/* Display existing images */}
              {device.images && device.images.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vorhandene Bilder ({device.images.length})
                  </Typography>
                  <Grid2
                    container
                    spacing={1}
                    sx={{
                      "& .MuiIconButton-root": {
                        color: "black",
                      },
                      "& .MuiTypography-root": {
                        color: "black",
                      },
                    }}
                  >
                    {device.images.map((image) => (
                      <Grid2 size={{ xs: 6, sm: 4, md: 3 }} key={image.id}>
                        <ImageThumbnail
                          image={image}
                          onDelete={handleDeleteImage}
                          onEdit={handleEditImage}
                          onOpen={handleOpenImage}
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
              )}
            </Box>

            {/* Document Upload Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Dokumente / Handbücher
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <StyledButton
                  buttonType="custom"
                  customIcon={<UploadFile />}
                  onClick={() => {
                    const input = document.createElement("input")
                    input.type = "file"
                    input.accept = ".pdf,.doc,.docx,.txt,.xlsx,.xls"
                    input.onchange = (e) => {
                      const files = (e.target as HTMLInputElement).files
                      if (files) {
                        handleDocumentUpload(Array.from(files))
                      }
                    }
                    input.click()
                  }}
                  variant="contained"
                  disabled={isUploadingDocument}
                >
                  Dokument hochladen
                </StyledButton>
              </Box>

              {/* Display existing documents as chips */}
              {device.documents && device.documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vorhandene Dokumente ({device.documents.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {device.documents.map((doc) => {
                      const isPdf = doc.type === FileType.PDF
                      const isImage = doc.type === FileType.IMAGE
                      const icon = isImage ? (
                        <ImageIcon />
                      ) : isPdf ? (
                        <PictureAsPdf />
                      ) : (
                        <InsertDriveFile />
                      )

                      return (
                        <Box
                          key={`existing-doc-${doc.id}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            backgroundColor: "#4c4c4c",
                            borderRadius: "5px",
                          }}
                        >
                          <Chip
                            icon={icon}
                            label={doc.name}
                            sx={{
                              backgroundColor: "#4c4c4c",
                            }}
                            onClick={() => handleOpenDocument(doc)}
                            onDelete={() => handleDeleteDocument(doc.id)}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleEditDocument(doc)}
                            aria-label="Dokument bearbeiten"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            {isEutGroup(device) && (
              <StyledButton
                buttonType="custom"
                onClick={() => setOpenAddNorms(true)}
                variant="contained"
              >
                Prüfverfahren hinzufügen/entfernen
              </StyledButton>
            )}

            {isEutGroup(device) && device.examinations && device.examinations.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  pb: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Zugeordnete Prüfverfahren
                </Typography>
                {Object.values(groupedExaminations)
                  .flatMap((group) => group.examinations)
                  .map((examination) => (
                    <Card
                      key={examination.id}
                      sx={{
                        mb: 1,
                        backgroundColor: "#353744",
                        color: "white",
                      }}
                    >
                      <CardHeader
                        title={
                          <Typography
                            sx={{
                              color: "white",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              lineHeight: 1.3,
                              fontSize: "1rem",
                            }}
                          >
                            {examination.name}
                          </Typography>
                        }
                        subheader={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {getNormName(examination.norm)}, {examination.norm.branch},{" "}
                              {examination.location.name}
                            </Typography>
                          </Box>
                        }
                        action={
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteExamination(examination.id)}
                              sx={{ color: "white" }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.3,
                                fontSize: "0.75rem",
                              }}
                            >
                              {examination.isAccredited ? (
                                <>
                                  <Check fontSize="small" color="success" />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "white",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    Akkr.
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Close fontSize="small" color="error" />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "white",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    Nicht Akkr.
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </Card>
                  ))}
              </Box>
            )}

            {isEutGroup(device) && (
              <AddNormsToProjectDialog
                open={openAddNorms}
                setOpen={setOpenAddNorms}
                onSave={handleChangeNormsAndExams}
                device={device}
              />
            )}
          </Box>

          {(isEutGroup(device) || isDeviceAux(device)) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: 1,
                overflowY: "auto",
                maxHeight: "calc(100vh - 250px)",
                marginTop: 1,
                pb: 10, // Add padding bottom to account for sticky CardActions
              }}
            >
              {device.deviceDataGroup.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    "& .MuiPaper-root": {
                      backgroundColor: "#3f414c",
                    },
                  }}
                >
                  <EUTDataCollapsible
                    group={item}
                    onSave={onSaveGroup}
                    isSaveDisabled={isSaveDisabled}
                    cardActions={
                      !isInExaminations(item) && !item.isDefault ? (
                        <IconButton
                          sx={{
                            color: "white",
                          }}
                          onClick={() => handleDeleteDeviceDataGroup(item)}
                        >
                          <Delete />
                        </IconButton>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>
              ))}
              <Autocomplete
                size="small"
                options={dataGroupsOptions}
                onChange={handleAddDeviceDataGroup}
                renderInput={(params) => (
                  <TextField {...params} label="EUT Eigenschaften hinzufügen" />
                )}
                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                getOptionLabel={(option) => option.name}
                sx={{ mt: 2 }}
                disabled={device.deviceDataGroup.length === deviceDataGroups.length}
              />
            </Box>
          )}
        </Box>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: "flex-end",
          position: "sticky",
          bottom: 0,
          backgroundColor: "transparent",
          borderTop: 1,
          borderColor: "divider",
          zIndex: 1000,
          backdropFilter: "blur(8px)",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <StyledButton buttonType="cancel" onClick={onCancel} variant="contained" />
        <StyledButton
          buttonType="save"
          onClick={handleSave}
          disabled={isSaveDisabled}
          variant="contained"
        />
      </CardActions>

      {/* Camera Dialog for photo capture */}
      <CameraDialog
        open={openCameraDialog}
        onClose={() => setOpenCameraDialog(false)}
        onSave={handleCameraCapture}
        imageName={device.name}
      />

      {/* Edit Image Dialog */}
      <Dialog open={editingImage !== null} maxWidth="sm" fullWidth>
        <DialogTitle>Bild bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Titel"
              value={editImageName}
              onChange={(e) => setEditImageName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Beschreibung (Was zeigt das Bild?)"
              value={editImageDescription}
              onChange={(e) => setEditImageDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="z.B. Vorderansicht des Geräts, Anschlüsse, Typenschild..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton buttonType="cancel" onClick={handleCancelImageEdit} variant="contained" />
          <StyledButton
            buttonType="save"
            onClick={handleSaveImageEdit}
            variant="contained"
            disabled={!editImageName}
          />
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bildanzeige</DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                component="img"
                src={getImageURL(previewImage.id)}
                alt={previewImage.name}
                sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editingDocument !== null} maxWidth="sm" fullWidth>
        <DialogTitle>Dokument bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Titel"
              value={editDocumentName}
              onChange={(e) => setEditDocumentName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Beschreibung (Was enthält das Dokument?)"
              value={editDocumentDescription}
              onChange={(e) => setEditDocumentDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="z.B. Bedienungsanleitung, Technische Spezifikation, Schaltplan..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton
            buttonType="cancel"
            onClick={handleCancelDocumentEdit}
            variant="contained"
          />
          <StyledButton
            buttonType="save"
            onClick={handleSaveDocumentEdit}
            variant="contained"
            disabled={!editDocumentName}
          />
        </DialogActions>
      </Dialog>
    </Card>
  )
}
